import React, {useEffect} from 'react';
import omit from 'lodash/omit';
import {browserHistory} from 'react-router';
import classnames from 'classnames';
import moment, {Moment} from 'moment';
import './DashboardDateRangePicker.styl';
import DashboardDateRangeActions from 'actions/dashboard/DasbhoardDateRangeActions';
import DateRange from 'components/pure/utils/form/DateRange';
import {addSubtractDayInterval} from '@store/coaching-plans';
import NextPreviousSelector from '@components/next-previous-selector';
import WebDataRocksOverlay from 'components/dashboard/WebDataRocksOverlay';

interface DashboardDateRangePickerProps {
  startDate: Moment;
  endDate: Moment;
  location: unknown; // react-router location.
}

/** Callback for next/previous links. */
function updatePeriod(
  operation: 'add' | 'subtract',
  startDate: Moment,
  endDate: Moment,
  maxDate: Moment
): void {
  const newRange = addSubtractDayInterval(operation, startDate, endDate, maxDate);
  if (newRange) {
    DashboardDateRangeActions.setDateRange(newRange);
  }
}

/**
 * Date range and next/previous widgets combined, with location-state-controlled blinking. Set `blinkDates` to make it
 * happen.
 *
 * @param startDate the start of the date range (inclusive).
 * @param endDate the end of the date range (inclusive).
 * @param location react-router location.
 * @constructor
 */
const DashboardDateRangePicker: React.FC<DashboardDateRangePickerProps> = ({
  startDate,
  endDate,
  location,
}) => {
  const maxDate = moment().startOf('day').subtract(1, 'day');
  const isNextDisabled = endDate.isSameOrAfter(maxDate);

  const previousCallback = () => updatePeriod('subtract', startDate, endDate, maxDate);
  const nextCallback = () => updatePeriod('add', startDate, endDate, maxDate);

  // TODO maybe we'll have types for this one day.
  // @ts-ignore
  const {pathname, query, state} = location;
  const blinkDates = state?.blinkDates || false;

  // Remove blinkDates from the state after the first render.
  useEffect(
    () => {
      if (!blinkDates) {
        return;
      }
      setTimeout(
        // timeout gives the animation time to render.
        () => {
          browserHistory.replace({
            pathname,
            query,
            state: omit(state, 'blinkDates'),
          });
        },
        2000 // animation lasts 2 seconds, so this is pretty much the minimum we can use here.
      );
    },
    // We don't really care whether this is called because of changes to pathname, query, or state, since it returns
    // immediately if blinkDates is false. They're just here because they're used in the function, and it makes the
    // linter happy.
    // Also, state is an object, so is only checked for reference equality. We don't care -- blinkDates is the thing
    // that determines whether this should run.
    [blinkDates, pathname, query, state]
  );

  return (
    <div className={classnames('dashboard-date-range', blinkDates ? 'blink' : '')}>
      <WebDataRocksOverlay startDate={startDate} endDate={endDate} />
      <DateRange
        label="From"
        startDate={startDate}
        endDate={endDate}
        maxDate={maxDate}
        onUpdateRange={DashboardDateRangeActions.setDateRange}
      />
      <NextPreviousSelector
        isNextDisabled={isNextDisabled}
        className="selector-item"
        onPrevious={previousCallback}
        onNext={nextCallback}
      />
    </div>
  );
};

export default DashboardDateRangePicker;
